import React, {useEffect, useMemo, useRef, useState} from "react";
import {OrderEditorTabProps} from "@widgets/pos/order/OrderEditor";
import CardHeader from "peggirkit/dist/components/Card/CardHeading/CardHeader";
import {
    Alert,
    ButtonGroup,
    ButtonGroupButton,
    Card,
    DatePicker,
    Icon,
    InputLabel,
    Select,
    SelectOptionProps,
    SimpleCardHeading,
} from "peggirkit";
import {CalendarDaysIcon, ClockIcon} from "@heroicons/react/20/solid";
import {FormattedMessage, useIntl} from "react-intl";
import {Controller, useFormContext} from "react-hook-form";
import dayjs from "dayjs";
import {PosOrderForm} from "@/data/entities/order";
import {useRestaurant} from "@/context/restaurant/RestaurantProvider";
import {useFulfillmentType} from "@widgets/pos/order/hooks/UseFulfillmentType";
import {fromLocalDateString, toLocalDateString} from "@/utils/DateUtils";
import {
    ASAP,
    laterDayTimeSlots,
    PRESENT,
    requestedDay,
    requestedTimeSlots,
    toRequestedTime,
} from "./RequestedTimeSlots";
import {nl} from "date-fns/locale";

const REFRESH_SLOTS_MS = 60_000;
/** Orders in advance can be placed at most this many months ahead. */
const MAX_MONTHS_AHEAD = 3;

/**
 * Requested delivery or pickup time: as soon as possible, or a time slot
 * today within the opening hours. Or, for an order in advance, any time on a
 * later day (the opening hours are not checked then).
 */
const RequestedTimeCard = ({isPending}: OrderEditorTabProps) => {
    const intl = useIntl();
    const {control, getValues, setValue} = useFormContext<PosOrderForm>();
    const {dashboardLocation} = useRestaurant();
    const fulfillmentType = useFulfillmentType();
    const [showDayPicker, setShowDayPicker] = useState(false);
    const [dayTooFarAhead, setDayTooFarAhead] = useState(false);

    // Slots in the past disappear while the editor is open
    const [now, setNow] = useState(dayjs());
    useEffect(() => {
        const interval = setInterval(() => setNow(dayjs()), REFRESH_SLOTS_MS);
        return () => clearInterval(interval);
    }, []);
    const today: string = now.format("YYYY-MM-DD");
    const lastDay: dayjs.Dayjs = now.add(MAX_MONTHS_AHEAD, "month");
    const lastDayFormatted: string = lastDay.format("YYYY-MM-DD");

    // Later day of an order in advance, or null for today. Starts with the
    // day of an existing order.
    const [laterDay, setLaterDay] = useState<string | null>(() => {
        const day = requestedDay(getValues("requestedTime"));
        return day !== null && day > dayjs().format("YYYY-MM-DD") ? day : null;
    });
    // Today again once that day has come (editor open past midnight)
    const activeLaterDay: string | null = laterDay !== null && laterDay > today ? laterDay : null;
    const isLaterDay: boolean = activeLaterDay !== null;

    const slots: string[] = useMemo(
        () => (activeLaterDay !== null
                ? laterDayTimeSlots(activeLaterDay)
                : requestedTimeSlots(dashboardLocation.openingWeek, fulfillmentType, now)
        ).map(toRequestedTime),
        [activeLaterDay, dashboardLocation.openingWeek, fulfillmentType, now],
    );

    // Delivery and takeaway hours differ: a chosen time may no longer be valid
    const previousFulfillmentType = useRef(fulfillmentType);
    useEffect(() => {
        if (previousFulfillmentType.current === fulfillmentType) return;
        previousFulfillmentType.current = fulfillmentType;

        // "Is already here" only exists for takeaway
        const requestedTime = getValues("requestedTime");
        if (requestedTime !== ASAP && !(requestedTime === PRESENT && fulfillmentType === "TAKEAWAY")
            && !slots.includes(requestedTime))
            setValue("requestedTime", isLaterDay ? "" : ASAP, {shouldDirty: true});
    }, [fulfillmentType, slots, isLaterDay, getValues, setValue]);

    // "Is already here" only exists for takeaway. Checked on every render of
    // the card: the fulfillment type is changed on another tab, so this card
    // may not see the change itself.
    useEffect(() => {
        if (fulfillmentType !== "TAKEAWAY" && getValues("requestedTime") === PRESENT)
            setValue("requestedTime", ASAP, {shouldDirty: true});
    }, [fulfillmentType, getValues, setValue]);

    /**
     * Switches to another day. Keeps the chosen clock time when possible;
     * otherwise the employee has to pick a time (or ASAP for today).
     */
    const changeDay = (day: string | null) => {
        const newLaterDay: string | null = day !== null && day > today ? day : null;
        setLaterDay(newLaterDay);
        setDayTooFarAhead(false);

        const current: string = getValues("requestedTime");
        const currentDay: string | null = requestedDay(current);
        const sameTime: string | null = currentDay === null
            ? null
            : toRequestedTime(dayjs(`${newLaterDay ?? today}T${dayjs(current).format("HH:mm")}`));

        if (newLaterDay !== null) {
            setValue("requestedTime", sameTime ?? "", {shouldDirty: true, shouldValidate: sameTime !== null});
            return;
        }

        const todaySlots: string[] = requestedTimeSlots(dashboardLocation.openingWeek, fulfillmentType, now)
            .map(toRequestedTime);
        if (current === "" || (currentDay !== null && currentDay !== today))
            setValue(
                "requestedTime",
                sameTime !== null && todaySlots.includes(sameTime) ? sameTime : ASAP,
                {shouldDirty: true},
            );
    };

    const toOption = (value: string): SelectOptionProps<undefined> => ({
        value,
        displayValue: value === ASAP
            ? intl.formatMessage({id: "pos.order.asap"})
            : value === PRESENT
                ? intl.formatMessage({id: "pos.order.present"})
                : dayjs(value).format("YYYY-MM-DD") === (activeLaterDay ?? today)
                    ? dayjs(value).format("HH:mm")
                    : dayjs(value).format("DD-MM HH:mm"),
    });

    const formattedLaterDay: string | null = activeLaterDay === null
        ? null
        : intl.formatDate(fromLocalDateString(activeLaterDay), {weekday: "short", day: "numeric", month: "short"});

    return (
        <Card
            header={
                <CardHeader>
                    <SimpleCardHeading
                        title={<FormattedMessage id={fulfillmentType === "DELIVERY"
                            ? "requestedDeliveryTime"
                            : "requestedPickupTime"}/>}
                    />
                </CardHeader>
            }
        >
            <div className={"flex flex-col gap-4"}>
                <ButtonGroup variant={"outline"}>
                    <ButtonGroupButton
                        iconBefore={<Icon icon={ClockIcon}/>}
                        disabled={isPending}
                        selected={!isLaterDay}
                        buttonAction={{
                            as: "button",
                            actionProps: {
                                onClick: e => {
                                    e.preventDefault();
                                    changeDay(null);
                                },
                            },
                        }}
                    >
                        <FormattedMessage id={"today"}/>
                    </ButtonGroupButton>
                    <ButtonGroupButton
                        iconBefore={<Icon icon={CalendarDaysIcon}/>}
                        disabled={isPending}
                        selected={isLaterDay}
                        buttonAction={{
                            as: "button",
                            actionProps: {
                                onClick: e => {
                                    e.preventDefault();
                                    setShowDayPicker(true);
                                },
                            },
                        }}
                    >
                        {formattedLaterDay ?? <FormattedMessage id={"pos.order.otherDay"}/>}
                    </ButtonGroupButton>
                </ButtonGroup>

                <Controller
                    control={control}
                    name={"requestedTime"}
                    rules={{required: intl.formatMessage({id: "pick-required-option"})}}
                    render={({field, fieldState}) => {
                        const values: string[] = isLaterDay
                            ? [...slots]
                            : fulfillmentType === "TAKEAWAY"
                                ? [ASAP, PRESENT, ...slots]
                                : [ASAP, ...slots];

                        // Keep a time that is no longer offered (e.g. an existing order) selectable
                        if (field.value && !values.includes(field.value))
                            values.push(field.value);
                        const options = values.map(toOption);

                        return (
                            <Select<undefined>
                                label={
                                    <InputLabel hint={<FormattedMessage id={"required"}/>}>
                                        <FormattedMessage id={"requested-time"}/>
                                    </InputLabel>
                                }
                                displayValueEmpty={intl.formatMessage({
                                    id: isLaterDay ? "pos.order.pickTime" : "pos.order.asap",
                                })}
                                options={options}
                                value={options.find(option => option.value === field.value) ?? null}
                                onChange={option => field.onChange(`${option.value}`)}
                                disabled={isPending}
                                error={fieldState.error?.message}
                            />
                        );
                    }}
                />

                {
                    isLaterDay &&
                    <Alert
                        type={"warning"}
                        title={<FormattedMessage id={"pos.order.laterDay"} values={{day: formattedLaterDay}}/>}
                        content={<FormattedMessage id={"pos.order.laterDayDescription"}/>}
                    />
                }

                {
                    !isLaterDay && slots.length === 0 &&
                    <Alert
                        type={"warning"}
                        title={<FormattedMessage id={"noTimeSlotsLeft"}/>}
                        content={<FormattedMessage id={"noTimeSlotsLeftDescription"}/>}
                    />
                }

                {
                    dayTooFarAhead &&
                    <Alert
                        type={"danger"}
                        title={<FormattedMessage id={"pos.order.dayTooFarAhead"} values={{months: MAX_MONTHS_AHEAD}}/>}
                    />
                }
            </div>

            <DatePicker
                title={intl.formatMessage({id: "pickADay"})}
                show={showDayPicker}
                setShow={setShowDayPicker}
                continueButton={{
                    text: intl.formatMessage({id: "apply"}),
                }}
                cancelButton={{
                    text: intl.formatMessage({id: "cancel"}),
                }}
                pickerConfig={{
                    type: "single",
                    defaultDay: activeLaterDay === null ? new Date() : fromLocalDateString(activeLaterDay),
                    onContinue: (selectedDate: Date | undefined) => {
                        if (selectedDate === undefined)
                            return;

                        // Also checked here, in case a day after the limit is picked anyway
                        const day: string = toLocalDateString(selectedDate);
                        setDayTooFarAhead(day > lastDayFormatted);
                        if (day > lastDayFormatted)
                            return;

                        // A day in the past (or today) is treated as today
                        changeDay(day);
                    },
                }}
                calendarLocale={nl}
                navigationEnabledFrom={dayjs().add(1, "day").toDate()}
                navigationEnabledTo={lastDay.toDate()}
            />
        </Card>
    );
};

export default RequestedTimeCard;